import { inRange } from "../../IntRangeUtils";

import type { Disposable } from "./AsyncPool";
import { AsyncPool } from "./AsyncPool";

interface Options<Item> {
    min: number;
    max: number;
    size: number;
    fabric: () => Item;
}

/**
 * Создаёт пул ограниченной ёмкости, удерживая кол-во элементов в установленных лимитах.
 *
 * @param options.min нижняя граница ёмкости;
 * @param options.max верхняя граница ёмкости;
 * @param options.size начальная ёмкость;
 * @param options.fabric фабрика элементов; вызывается ровно `size` раз при создании.
 *
 * @throws если `size < min` или `size > max`.
 */
export class LimitedPool<Item extends Disposable> {
    private size: number;
    private readonly min: number;
    private readonly max: number;
    private readonly pool: AsyncPool<Item>;

    public constructor(options: Options<Item>) {
        const { size, min, max, fabric } = this.validateOptions(options);
        this.size = size;
        this.min = min;
        this.max = max;
        this.pool = new AsyncPool();
        this.exec = this.pool.exec.bind(this.pool);
        this.dispose = this.pool.dispose.bind(this.pool);
        this.init(this.pool, size, fabric);
    }

    /** Прокси на {@link AsyncPool.exec} */
    public exec: AsyncPool<Item>["exec"];

    /** Прокси на {@link AsyncPool.dispose} */
    public dispose: AsyncPool<Item>["dispose"];

    /**
     * Добавляет элемент в пул, если текущая ёмкость ниже `max`.
     */
    public add(item: Item): void {
        if (this.size < this.max) {
            this.pool.add(item);
            this.size++;
        }
    }

    /**
     * Уменьшает ёмкость пула на один элемент, если текущая ёмкость выше `min`.
     *
     * @returns `true`, если ёмкость была уменьшена; `false`, если пул уже на минимуме.
     */
    public evict(): boolean {
        const aboveTheLimit = this.size > this.min;
        if (aboveTheLimit && this.pool.evict()) {
            this.size--;
            return true;
        }
        return false;
    }

    private validateOptions(options: Options<Item>): Options<Item> {
        if (inRange(options.size, options.min, options.max)) {
            return options;
        } else {
            throw new Error("LimitedPool: size must be between min and max");
        }
    }

    private init(pool: AsyncPool<Item>, size: number, fabric: () => Item): void {
        const initial = Array.from({ length: size }, fabric);
        initial.forEach(i => pool.add(i));
    }
}
